import { useEffect, useState, useCallback } from 'react';
import { Dialog, Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { getCobroPorId } from '../../services/cobranzaService';
import { colorPrimarioTramite } from '../Documentos/coloresTramite';
import { ChipEstadoCobro } from './estadoCobro';
import DetalleCobranza from './DetalleCobranza';

const COLOR = colorPrimarioTramite('cobro');

/**
 * El cobro en un modal, para los listados (A cobrar, Altas, Deudores) y la tarjeta de cobros de la
 * ficha del cliente. Adentro va DetalleCobranza: el mismo detalle que se ve en la ficha del trámite,
 * con las mismas secciones (ya no hay pestañas).
 *
 * El spinner es solo de la primera carga. Al guardar algo se vuelve a pedir el cobro pero el detalle
 * sigue a la vista con los datos anteriores hasta que llegan los nuevos, así no parpadea ni cierra
 * lo que la persona tenía abierto.
 */
export default function ModalDetalleCobro({ open, idCobro, onClose, onCambio }) {
    const [cobro, setCobro] = useState(null);

    const cargar = useCallback(() => {
        if (!idCobro) return;
        getCobroPorId(idCobro)
            .then(setCobro)
            .catch(() => toast.error('No se pudo cargar el cobro.'));
    }, [idCobro]);

    useEffect(() => {
        if (open && idCobro) {
            // Se descarta el cobro anterior: si no, al abrir otro se vería un instante el que se miró antes.
            setCobro(null);
            cargar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, idCobro]);

    const avisarCambio = () => {
        cargar();
        onCambio?.();
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}>
            <Box sx={{
                background: `${COLOR}12`,
                borderBottom: `1px solid ${COLOR}33`,
                px: 3, py: 2.5,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: `${COLOR}1F`, color: COLOR, fontSize: '1.3rem',
                    }}>
                        <i className="bi bi-cash-coin"></i>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <div className="fw-bold" style={{ color: '#1e293b', fontSize: '1.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cobro ? [cobro.apellido, cobro.nombre].filter(Boolean).join(', ') : 'Cobro'}
                        </div>
                        <div className="text-muted small">{cobro?.cuil}</div>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    {cobro && <ChipEstadoCobro estado={cobro.estado} />}
                    <IconButton onClick={onClose} size="small" aria-label="Cerrar"><CloseIcon fontSize="small" /></IconButton>
                </Box>
            </Box>

            {!cobro ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: COLOR }} /></Box>
            ) : (
                // El detalle es más alto que la pantalla: el encabezado queda fijo y solo el cuerpo se
                // desplaza (el papel del diálogo tiene overflow hidden, así que sin esto se corta).
                <Box sx={{ p: 3, overflowY: 'auto' }}>
                    <DetalleCobranza cobro={cobro} onCambio={avisarCambio} />
                </Box>
            )}
        </Dialog>
    );
}
